import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSave, FaArrowLeft } from 'react-icons/fa';
import { AiOutlineProduct } from 'react-icons/ai';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import { compressFiles, optimizeFormData, validateFormData } from '../../../shared/utils/performanceUtils';
import {
  addProducts,
  clearState,
  fetchStoreProfile,
} from '../../../redux/Slices/ProductsSlice';
import "./products.css";
import axios from 'axios';
const BASE_URL = process.env.REACT_APP_BASE_URL;
const AddProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [allImages, setAllImages] = useState([]);
  const fileInputRef = useRef(null);
  const [imageProcessing, setImageProcessing] = useState(false);

  const [unitsData, setUnitsData] = useState([
    {
      unit_name: "",
      price: "",
      quantity: "",
      colors: [],
    },
  ]);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    quantity: "",
    subcategory_id: "",
    images: [],
    units: [],
    description: "",
  });

  const { isLoading, error, success, storeProfile } = useSelector(
    (state) => state.products
  );
  const [units, setUnits] = useState([]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchStoreProfile());
    fetchUnitsFromApi();
  }, [dispatch]);

  const getAuthToken = () => {
    try {
      const s = sessionStorage.getItem('storekhadamat');
      if (!s) return null;
      const parsed = JSON.parse(s);
      return parsed?.token || null;
    } catch { return null; }
  };

  const fetchUnitsFromApi = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${BASE_URL}/category_unit`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept-Language': 'ar', 'lang': 'ar' }
      });
      const list = res.data?.data || res.data?.result || [];
      setUnits(Array.isArray(list) ? list : []);
    } catch (e) {
      showErrorToast('فشل في جلب الوحدات');
    }
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // منع القيم السالبة في حقول السعر
    if (name === 'price' && value !== '' && parseFloat(value) < 0) {
      showErrorToast('السعر لا يمكن أن يكون سالباً');
      return;
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleColorChange = (unitIndex, colorIndex, e) => {
    const { name, value } = e.target;
    
    // منع القيم السالبة في حقول السعر
    if (name === 'price' && value !== '' && parseFloat(value) < 0) {
      showErrorToast('السعر لا يمكن أن يكون سالباً');
      return;
    }
    
    setUnitsData((prev) => {
      const updated = [...prev];
      updated[unitIndex].colors[colorIndex][name] = value;
      return updated;
    });
  };
  const handleUnitChange = (unitIndex, e) => {
    const { name, value } = e.target;
    
    // منع القيم السالبة في حقول السعر
    if (name === 'price' && value !== '' && parseFloat(value) < 0) {
      showErrorToast('السعر لا يمكن أن يكون سالباً');
      return;
    }
    
    setUnitsData((prev) => {
      const updated = [...prev];
      updated[unitIndex][name] = value;
      return updated;
    });
  };
  const handleAddUnit = () => {
    setUnitsData((prev) => [
      ...prev,
      {
        unit_name: "",
        price: "",
        quantity: "",
        colors: [],
      },
    ]);
  };

  const handleRemoveUnit = (index) => {
    setUnitsData((prev) => prev.filter((_, i) => i !== index));
  };

  // Add new empty row
  const handleAddColor = (unitIndex) => {
    setUnitsData((prev) => {
      const updated = [...prev];
      updated[unitIndex].colors.push({
        color_code: "#000000",
        price: "",
        quantity: "",
      });
      return updated;
    });
  };

  const handleRemoveColor = (unitIndex, colorIndex) => {
    setUnitsData((prev) => {
      const updated = [...prev];
      updated[unitIndex].colors = updated[unitIndex].colors.filter(
        (_, i) => i !== colorIndex
      );
      return updated;
    });
  };

  const hasUnitData = unitsData.some((unit) =>
    unit.colors.some((c) => c.color_code || c.price || c.quantity)
  );
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    for (let file of files) {
      if (!validTypes.includes(file.type)) {
        showErrorToast("الملفات يجب أن تكون صور (jpg, jpeg, png)");
        e.target.value = "";
        return;
      }
    }

    setImageProcessing(true);

    try {
      // ضغط الصور لتحسين الأداء
      const compressedFiles = await compressFiles(files, { 
        maxSize: 500000, // 500KB
        quality: 0.8 
      });

      // Add files to formData for submission
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...compressedFiles],
      }));

      // Add files to allImages for preview
      const newImages = compressedFiles.map((file) => ({
        id: null,
        image_url: URL.createObjectURL(file), // preview
        isNew: true,
        file,
        toDelete: false,
      }));

      setAllImages((prev) => [...prev, ...newImages]);
    } catch (error) {
      showErrorToast("فشل في معالجة الصور");
    } finally {
      setImageProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (imageProcessing) {
      showErrorToast("يرجى الانتظار حتى يتم تجهيز الصور قبل الإرسال");
      return;
    }

    const hasImagesToSubmit = allImages.some((img) => !img.toDelete);
    if (!hasImagesToSubmit) {
      showErrorToast("يرجى إضافة صورة واحدة على الأقل للمنتج");
      return;
    }

    dispatch(clearState());
    
    const filteredImages = allImages
      .filter((img) => !img.toDelete && img.isNew)
      .map((img) => img.file);
    
    // Build unitsPayload directly from unitsData
    const unitsPayload = unitsData.map((unit) => {
      // find selected unit details
      const selectedUnit = units.find((u) => u.id === Number(unit.unit_name));

      const colorsPayload =
        unit.colors && unit.colors.length > 0
          ? unit.colors
              .filter(
                (c) =>
                  (c.color_code && c.color_code.trim() !== '') ||
                  (c.price !== undefined && c.price !== null && c.price !== '') ||
                  (c.quantity !== undefined && c.quantity !== null && c.quantity !== '')
              )
              .map((c) => ({
                color_code:
                  c.color_code && c.color_code.trim() !== ''
                    ? c.color_code.trim()
                    : '#000000',
                price:
                  c.price !== undefined && c.price !== null && c.price !== ''
                    ? c.price
                    : unit.price || formData.price,
                quantity:
                  c.quantity !== undefined && c.quantity !== null && c.quantity !== ''
                    ? c.quantity
                    : unit.quantity || formData.quantity,
              }))
          : [];

      return {
        unit_type: selectedUnit?.type || "",
        unit_name: unit.unit_name,
        price: unit.price || formData.price,
        quantity: unit.quantity || formData.quantity,
        colors: colorsPayload,
      };
    });
    
    // Final payload
    const formPayload = new FormData();
    formPayload.append("name", formData.name);
    formPayload.append("subcategory_id", storeProfile.subcategory_id);
    formPayload.append("price", formData.price);
    formPayload.append("description", formData.description);

    // append units as JSON string
    unitsPayload.forEach((unit, i) => {
      formPayload.append(`units[${i}][unit_type]`, unit.unit_type);
      formPayload.append(`units[${i}][unit_name]`, unit.unit_name);
      formPayload.append(`units[${i}][price]`, unit.price);
      formPayload.append(`units[${i}][quantity]`, unit.quantity);

      unit.colors.forEach((color, j) => {
        formPayload.append(
          `units[${i}][colors][${j}][color_code]`,
          color.color_code
        );
        formPayload.append(`units[${i}][colors][${j}][price]`, color.price);
        formPayload.append(
          `units[${i}][colors][${j}][quantity]`,
          color.quantity
        );
      });
    });

    // append images
    filteredImages.forEach((file) => {
      formPayload.append("images[]", file);
    });

    // تحسين FormData
    const optimizedPayload = optimizeFormData(formPayload);
    
    // التحقق من صحة البيانات
    const validation = validateFormData(optimizedPayload, ['name', 'description']);
    if (!validation.isValid) {
      showErrorToast(validation.errors.join(', '));
      return;
    }

    dispatch(addProducts(optimizedPayload));
  };

  useEffect(() => {
    if (success) {
      showSuccessToast("تمت الإضافة بنجاح");
      setTimeout(() => {
        dispatch(clearState());
        navigate("/view-products");
      }, 500);
    }
    if (error) {
      showErrorToast(error || "لم تتم الإضافة");
      // تأخير clearState لضمان ظهور التوست
      setTimeout(() => {
        dispatch(clearState());
      }, 2000);
    }
  }, [success, error, dispatch, navigate]);

  const hasActiveImages = allImages.some((img) => !img.toDelete);
  const disableSubmit = isLoading || imageProcessing || !hasActiveImages;
 
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              {" "}
              <h4 style={{ fontSize: "1.1rem", margin: 0 }}>
                {" "}
                <AiOutlineProduct
                  className="mx-2"
                  style={{ fontSize: "1.5rem", color: "#6b7280" }}
                />{" "}
                إضافة منتج جديد
              </h4>
            </div>
            <div>
              <Link to="/view-products" className="text-decoration-none mx-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn btn-outline-secondary btn-sm"
                  style={{ borderRadius: "8px" }}
                >
                  العودة
                  <FaArrowLeft className="mx-2" />
                </motion.button>
              </Link>
            </div>
          </div>

          <div className="card p-3">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="name" className="form-label">
                      الاسم *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="الاسم"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="price" className="form-label">
                      السعر
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="form-control"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="السعر"
                      required={!hasUnitData}
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label htmlFor="quantity" className="form-label">
                      الكمية *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      placeholder="الكمية"
                      required
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label htmlFor="description" className="form-label">
                      الوصف *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="الوصف"
                      required
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label htmlFor="images" className="form-label">
                      صور *
                    </label>
                    <input
                      type="file"
                      multiple
                      className="form-control"
                      ref={fileInputRef}
                      accept="image/png, image/jpeg, image/jpg"
                      id="images"
                      name="images"
                      onChange={handleFileChange}
                      disabled={imageProcessing}
                      required
                    />
                    {imageProcessing && (
                      <div className="alert alert-info py-2 px-3 mt-2">
                        جاري تجهيز الصور، يرجى الانتظار...
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {allImages.map((img, idx) => (
                        <div key={idx} className="position-relative">
                          <img
                            src={img.image_url}
                            alt="preview"
                            className="img-thumbnail"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                            }}
                          />
                          <button
                            type="button"
                            className={`btn btn-sm position-absolute top-0 end-0 ${
                              img.toDelete ? "btn-secondary" : "btn-danger"
                            }`}
                            onClick={() => {
                              setAllImages((prev) => {
                                const updated = prev.filter(
                                  (_, i) => i !== idx
                                );

                                if (
                                  updated.length === 0 &&
                                  fileInputRef.current
                                ) {
                                  fileInputRef.current.value = "";
                                  setFormData((p) => ({ ...p, images: [] }));
                                }

                                return updated;
                              });
                            }}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {unitsData.map((unit, unitIndex) => (
                    <div
                      key={unitIndex}
                      className="border p-3 mb-3 rounded bg-light"
                    >
                      <h6>الوحدة {unitIndex + 1}</h6>

                      {/* Select unit name */}
                      <select
                        name="unit_name"
                        value={unit.unit_name}
                        onChange={(e) => handleUnitChange(unitIndex, e)}
                        className="form-select mb-2"
                        required
                      >
                        <option value="" disabled>
                          اختر الوحدة
                        </option>
                        {units.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>

                      <input
                        type="number"
                        min="1"
                        step="1"
                        name="price"
                        value={unit.price}
                        onChange={(e) => handleUnitChange(unitIndex, e)}
                        placeholder="السعر"
                        className="form-control mb-2"
                      />
                      <input
                        name="quantity"
                        value={unit.quantity}
                        onChange={(e) => handleUnitChange(unitIndex, e)}
                        placeholder="الكمية"
                        className="form-control mb-2"
                      />

                      {/* Colors */}
                      {unit.colors.map((color, colorIndex) => (
                        <div key={colorIndex} className="d-flex gap-2 mb-2">
                          <span className="mt-1">اللون</span>
                          <input
                            type="color"
                            name="color_code"
                            className="mt-1"
                            value={color.color_code}
                            onChange={(e) =>
                              handleColorChange(unitIndex, colorIndex, e)
                            }
                          />
                          <input
                            type="number"
                            min="1"
                            step="1"
                            name="price"
                            value={color.price}
                            onChange={(e) =>
                              handleColorChange(unitIndex, colorIndex, e)
                            }
                            placeholder="سعر اللون"
                            className="form-control"
                          />
                          <input
                            name="quantity"
                            value={color.quantity}
                            onChange={(e) =>
                              handleColorChange(unitIndex, colorIndex, e)
                            }
                            placeholder="كمية اللون"
                            className="form-control"
                          />
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() =>
                              handleRemoveColor(unitIndex, colorIndex)
                            }
                          >
                            -
                          </button>
                          {colorIndex === unit.colors.length - 1 && (
                            <button
                              type="button"
                              className="btn btn-success"
                              onClick={() => handleAddColor(unitIndex)}
                            >
                              +
                            </button>
                          )}
                        </div>
                      ))}

                      <div className="d-flex justify-content-end gap-2 flex-wrap mt-3">
                        {unit.colors.length === 0 && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm px-3"
                            onClick={() => handleAddColor(unitIndex)}
                          >
                            + إضافة لون
                          </button>
                        )}
                      <button
                        type="button"
                          className="btn btn-outline-danger btn-sm px-3"
                        onClick={() => handleRemoveUnit(unitIndex)}
                      >
                        حذف الوحدة
                      </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    style={{ fontSize: "small" }}
                    className="btn btn-outline-primary"
                    onClick={handleAddUnit}
                  >
                    + إضافة وحدة
                  </button>
                </div>

                <div className="d-flex justify-content-center gap-2 mt-4">
                  <motion.button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/view-products")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    إلغاء
                  </motion.button>
                  <motion.button
                    type="submit"
                    className="btn btn-primary d-flex align-items-center"
                    disabled={disableSubmit}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mx-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        جاري الحفظ...
                      </>
                    ) : (
                      <>
                        <FaSave className="mx-2" />
                        حفظ
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;
